// #/types/workspace.ts
import { Node, Edge } from 'reactflow';
import find from 'lodash/find';
import { v4 as uuidv4 } from 'uuid';

export type Prerequisite = 'OAUTH_1_0A';

export interface WorkflowCronTab {
  [cronSchedule: string]: { [workflowSnapshotUuid: string]: boolean };
}

export interface IWorkspace {
  id?: string; //postgres id

  uuid: string; //uuid
  context?: Record<string, any>;
  prerequisites?: Prerequisite[];

  nodes?: INode[] | [] | undefined;
  edges?: Edge[] | [];

  hash?: string; //hash
  contract?: string; //contract address
  owner?: string; //wallet
  name?: string; //title
  label?: string; //subtitle
  description?: string; //markdown
  members?: string[]; //wallets
  icons?: string[];
  type?: any | null;
  data?: any | null;
  category?: any | null;
  tags?: any | null; //#hashtags
  origin?: string; //the id of the hash that this workspace was forked from
  origins?: string[]; //the ids of the hashes that this workspace was forked from
  dna?: string; //the id of the hash that this workspace sythesized from
  dnas?: string[]; //the ids of the hashes that this workspace sythesized from
  archived?: boolean;
  public?: boolean;
  shared?: boolean; //merkle root
  sharedWith?: string[]; //wallets
  sharedWithGroups?: string[]; //group ids
  sharedWithOrganizations?: string[]; //organization ids
  secret?: string; //Lit signed secret/public key pair
  secretKey?: string; //Lit signed secret/public key pair

  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

export interface IWorkflow {
  id?: string; //postgres id
  uuid: string; //uuid
  hash?: string; //hash
  context?: Record<string, any>;
  prerequisites?: Prerequisite[];
  nodes: INode[] | [] | undefined;
  edges: Edge[] | [];

  owner?: string; //wallet
  name?: string; //title
  label?: string; //subtitle
  description?: string; //markdown
  icons?: string[];
  inputs?: any; //inputs to the workspace
  outputs?: any; //outputs from the workspace

  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

export function workflowFromWorkspace(workspace?: IWorkspace): IWorkflow {
  return {
    uuid: workspace?.uuid,
    hash: workspace?.hash,
    context: workspace?.context,
    nodes: workspace?.nodes,
    edges: workspace?.edges,

    owner: workspace?.owner,
    name: workspace?.name,
    label: workspace?.label,
    description: workspace?.description,
    icons: workspace?.icons,

    createdAt: workspace?.createdAt,
    updatedAt: workspace?.updatedAt,
    deletedAt: workspace?.deletedAt,
  } as IWorkflow;
}

type DeploymentType = 'TEST' | 'DEPLOYED';
type EnabledStatus = 'ENABLED' | 'DISABLED';

export interface IWorkflowSnapshot {
  id?: string; //postgres id
  uuid: string; //uuid
  hash?: string; //hash
  workflowUuid: string;
  timestamp: number;
  uri?: string;
  json: IWorkflow | Record<string, any>;
  owner?: string; //wallet
  cronSchedule?: string;
  deploymentType: DeploymentType;
  enabledStatus: EnabledStatus;

  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

const findNode = (workflowSnapshotJson: IWorkspace, nodeQuery: any) => {
  return find(workflowSnapshotJson.nodes, nodeQuery);
};

function cronScheduleFromWorkflow(args: { workflow: IWorkflow }) {
  const { workflow } = args;
  const cronNode = findNode(workflow, {
    type: 'trigger',
    triggerType: 'cron',
  });
  return cronNode ? cronNode?.data?.inputValues?.schedule : null;
}

export function createWorkflowSnapshot(args: {
  iWorkflow: IWorkflow;
  iWorkflowSnapshot?: Partial<IWorkflowSnapshot>;
}): IWorkflowSnapshot {
  const date = new Date();
  const timestampMs = date.getTime();
  const timestampIso = date.toISOString();
  const { iWorkflow, iWorkflowSnapshot } = args;
  return {
    // DEFAULT VALUES
    uuid: uuidv4(),
    hash: iWorkflow?.hash,
    workflowUuid: iWorkflow?.uuid,
    timestamp: timestampMs,
    json: iWorkflow,
    owner: iWorkflow?.owner,
    cronSchedule: cronScheduleFromWorkflow({ workflow: iWorkflow }),
    deploymentType: 'TEST',
    enabledStatus: 'ENABLED',
    createdAt: timestampIso,
    updatedAt: timestampIso,

    // OVERWRITE DEFAULTS WITH PROVIDED VALUES
    ...iWorkflowSnapshot,
  };
}

export type NodeType = 'turbo';

// export type NodeDataType = 'trigger' | 'action';

export interface NodeData {
  uuid: string;
  // type: NodeDataType;
  title: string;
  subline: string;
  icons: string[];
  isLateral: boolean;
  logoUrl?: string;
}

export interface TriggerData {
  triggerType: TriggerType;
  inputSchema?: Record<string, any>;
  inputValues: Record<string, any>;
}

export type TriggerType = 'cron';
export interface TriggerNodeData extends NodeData, TriggerData {
  // NodeData
  // ...
  type: 'trigger';

  // TriggerData
  // ...
}

export interface ActionData {
  serviceKey: string;
  actionKey: string;
  actionVerb: string;
  actionEndpoint: string;
  context?: Record<string, any>;
  prerequisites?: Prerequisite[];
  inputSchema?: Record<string, any>;
  inputValues?: {
    headers: Record<string, any>;
    path: Record<string, any>;
    query: Record<string, any>;
    bodyJson: Record<string, any>;
  };
  outputSchema?: Record<string, any>;
  outputValues?: Record<string, any>;
}

export interface ActionNodeData extends NodeData, ActionData {
  // NodeData
  // ...
  type: 'action';

  // ActionData
  // ...
}

export type TriggerNode = Node<TriggerNodeData>;
export type ActionNode = Node<ActionNodeData>;
export type INodeData = TriggerNodeData | ActionNodeData;
export type INode = Node<INodeData>;

export type RunStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'SUCCESS'
  | 'ERROR'
  | 'FAILED'
  | 'CANCELED';

// TODO: xState
// export type IWorkflowRunSM;

export interface IWorkflowRun {
  id?: string; //postgres id
  uuid: string; //uuid
  workflowSnapshotUuid: string; //uuid
  deploymentType: DeploymentType;
  // json: IWorkflowRunSM;
  previousWorkflowTaskRunId: string;
  currentWorkflowTaskRunId: string;
  terminalWorkflowTaskRunId: string;
  status: RunStatus;
  error: Error;

  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

export interface IWorkflowTaskRun {
  id?: string; //postgres id
  uuid?: string; //uuid
  workflowRunUuid: string; //uuid
  nodeId?: string; //workflow node id
  nodeUuid?: string; //workflow node uuid

  serviceKey: string;
  actionKey: string;
  actionVerb: string;
  actionEndpoint: string;
  status: RunStatus;
  input: Record<string, any>;
  output: Record<string, any>;
  error: Error;

  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

export type Task = {
  id: string; //uuid
  hash: string; //ipfs hash
  contract: string; //contract address
  owner: string; //wallet
  name: string; //title
  label: string; //subtitle
  description: string; //markdown
};

export type Service = {
  id: string; //uuid
  hash: string; //ipfs hash
  contract: string; //contract address
  owner: string; //wallet
  name: string; //title
  label: string; //subtitle
  description: string; //markdown
  tags: string[];
  category: string[];
  type: string;
  data: string;
  tasks: Array<Task>;
};

export type Organization = {
  id: string; //uuid
  hash: string; //ipfs hash
  contract: string; //contract address
  owner: string;
  name: string; //title
  label: string; //subtitle
  description: string; //markdown
  members: string[]; //wallets
  icons: string[];
  nodes: Node[] | [];
  edges: Edge[] | [];
  type: any | null;
  data: any | null;
  category: any | null;
  tags: any | null; //#hashtags
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};
